import get from 'lodash.get';
import { ofType } from 'redux-observable';
import { EMPTY, Observable, of, concat, merge, from } from 'rxjs';
import { filter, catchError, map, mergeMap, switchMap, throttleTime, tap, take, takeUntil } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService } from 'ui-components/services/notification-service';

import * as Actions from './create-label-content.actions';
import { SubscriptionRequiredModal } from '../../components/modals/subscription-required-modal';
import { CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { CSWTypes } from '../../enums/csw-types';
import { DimensionUnit } from '../../enums/dimension-unit';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { Providers } from '../../enums/providers';
import { LabelBatchStatusStep } from '../../enums/label-batch-status-step';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { getShipFromIdOrDefault } from '../../fulfillment-planner';
import { createLabelSubject } from '../../fulfillment-planner/context';
import { getIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';
import { launchShipsuranceTermsModalWaitToProceed } from '../../services/insurance-terms-helper';
import { hasUserAcceptedInsuranceTerms } from '../../services/insurance-terms-service';
import { launchUpsLabelTermsModal } from '../../services/ups-label-terms-service';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../wallet/utilities/constants';
import { checkUpsLabelTermsRequired } from '../seller/seller.api';
import { ShipmentConfigurationService } from '../../services/shipping-configuration-service';
import { getPlansWithDangerousGoods, trackLabelCreationComplete, trackLabelCreationError } from '../../store/labels/tracking-events';
import * as BulkActions from '../bulk/bulk.actions';
import { makeBulkCreateLabelObservable } from '../bulk/bulk.label.observables';
import * as BulkPurchaseActions from '../bulk/purchase/bulk.purchase.actions';
import { startBulkPurchaseCostReview, waitForEndOfCostReview } from '../bulk/purchase/bulk.purchase.cost-review.observables';
import * as CSWActions from '../csw/csw.actions';
import { runNowAndRetryAfterCostReview } from '../csw/csw.cost-review.observables';
import { getErrorMessagesFromSaveConfigAndRateResponse } from '../csw/csw.error-helpers';
import * as Helpers from '../csw/csw.helpers';
import * as Builders from '../csw/csw.request-builders';
import * as CustomersCreateLabelActions from '../customers/create-label/customers.create-label.actions';
import Global from '../global-selectors';
import * as PostagePurchaseActions from '../postage-purchase/postage-purchase.actions';
import * as PrintActions from '../printing/print.actions';
import * as Api from '../rate-browser/rate-browser.api';
import * as ReturnsDetailsActions from '../returns/details/returns.details.actions';
import * as ReturnsActions from '../returns/returns.actions';
import * as ShipmentDetailsActions from '../shipments/shipment-details/shipments.details.actions';

const showUpsTermsModalAndWait$ = (nextActionCreator) => {
  return new Observable((observer) => {
    const modalId = launchUpsLabelTermsModal(
      () => {
        observer.next(nextActionCreator());
        observer.complete();
      },
      () => {
        observer.complete();
      },
    );
    return () => ModalService.closeModalById(modalId, true);
  });
};

const checkUpsTermsRequiredForReturn$ = ({ state, fulfillmentPlanId }) => {
  const { enableUpsTerms2 } = new FeatureFlags({ state });

  if (!enableUpsTerms2) {
    return of({ needsTerms: false });
  }

  const csw = Global.selectCSWByFulfillmentPlanId(state, { fulfillmentPlanId });
  const sellerLabelProviderId = csw?.shippingConfiguration?.sellerLabelProviderId;

  if (sellerLabelProviderId == null) {
    return of({ needsTerms: false });
  }

  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  const sellerLabelProvider = sellerLabelProviders?.find((slp) => slp.sellerLabelProviderId === sellerLabelProviderId);

  if (!sellerLabelProvider || sellerLabelProvider.labelProviderId !== Providers.UPSWallet.labelProviderId) {
    return of({ needsTerms: false });
  }

  const hasAcceptedUpsTermsSetting = Global.selectSellerSellerSetting(state, { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING });

  if (hasAcceptedUpsTermsSetting === 'true') {
    return of({ needsTerms: false });
  }

  return from(checkUpsLabelTermsRequired()).pipe(
    map((response) => ({ needsTerms: response.termsRequired })),
    catchError(() => of({ needsTerms: true })),
  );
};

const configureRatesAction = {
  'rate-browser': Global.selectRateBrowsergetConfigureRatesModel,
  'customers-create-label': Global.selectCustomersCreateLabelConfigureRatesModel,
};

export const configureLabel = (action$, { getState }) => {
  return action$.pipe(
    // also hits rate browser reducer to show create label view
    ofType(Actions.CREATE_LABEL_CONTENT_CONFIGURE_LABEL),
    mergeMap(({ sellerLabelProviderId, rate, fulfillmentPlanId }) => {
      let address;
      let cswType;
      let emailAddress = { emailAddress: '' };
      const state = getState();
      const configureRatesModel = configureRatesAction[fulfillmentPlanId](state);
      const sellerDimensionUnit = Global.selectSellerDefaultDimensionUnit(state);
      const defaultCustomsDeclarationSetting = Global.selectSellerSellerSetting(state, { settingName: 'CreateCustomsItems' });
      const createCustomsUsingPredefinedValues = 'static';
      const customsData = Global.selectSellerDefaultCustomData(state);
      const defaultCustomsItems = [
        {
          customsItemId: null,
          description: customsData.itemDescription,
          quantity: parseInt(customsData.itemQty, 10),
          unitPrice: {
            value: parseInt(customsData.itemValue, 10),
            code: 'USD',
          },
          harmonizedTariffCode: customsData.itemHarmonization,
          originCountryCode: customsData.itemCountry,
          fulfillmentPlanItemId: null,
        },
      ];
      const customsItems = defaultCustomsDeclarationSetting === createCustomsUsingPredefinedValues ? defaultCustomsItems : [];

      const { serviceId, serviceCollectionGuid } = rate;
      let { packageTypeId, packageId } = configureRatesModel;
      const { shipToPostalCode, shipToCountryCode, shipToCity, weight, dimensions, confirmationType } = configureRatesModel;
      if (fulfillmentPlanId === 'rate-browser') {
        cswType = CSWTypes.rateBrowser.id;
        address = {
          postalCode: shipToPostalCode,
          countryCode: shipToCountryCode,
          city: shipToCity,
          name: '',
          line1: '',
          line2: '',
          line3: null,
          state: '',
          company: '',
          phone: '',
        };
        packageTypeId = rate.packageTypeId;
        packageId = rate.packageId;
      }
      if (fulfillmentPlanId === 'customers-create-label') {
        const selectedCustomerId = (Global.selectSelectedCustomersIds(state) || emptyArray)[0];
        const customerEntities = Global.selectCustomersGridCustomers(state);
        const customerEntity = customerEntities.find((customer) => customer.customerId === selectedCustomerId) || emptyObject;
        address = customerEntity.address;
        emailAddress = { emailAddress: customerEntity.email };
        cswType = CSWTypes.customerCreatelabel.id;
      }
      if (getIsServiceLocalDelivery(state, serviceId)) {
        cswType = CSWTypes.localDeliveryRate.id;
      }
      const shipFroms = Global.selectSellerShipFroms(state);
      const shipFromId = getShipFromIdOrDefault(configureRatesModel.shipFromId, shipFroms);
      const configureShipmentPayload = {
        shipFromId,
        serviceId,
        sellerLabelProviderId,
        serviceCollectionGuid,
        packages: [
          {
            weight: {
              unit: weight.weightUnit || 'Ounces',
              value: weight.weight || 0,
            },
            dimensions: {
              ...dimensions,
              unit: dimensions.unit === DimensionUnit.Unknown ? sellerDimensionUnit : dimensions.unit,
            },
            packageTypeId,
            packageId,
          },
        ],
        options: {
          confirmationEnumId: confirmationType,
        },
        customs: {
          content: null,
          nonDelivery: null,
          customsItems,
        },
        availableSellerShippingServices: [parseInt(serviceId, 10)],
        shipTo: address,
        email: emailAddress.emailAddress,
      };
      return of(
        CSWActions.setupShipmentConfiguration(fulfillmentPlanId, configureShipmentPayload, { type: cswType }),
        CSWActions.startUpdateAvailableShippingServices(fulfillmentPlanId, shipFromId),
      );
    }),
  );
};

const endProcessingSuccessForOutboundLabel = (
  state,
  { doNotPrint, fulfillmentPlanId, labelObject = emptyObject, originatingFulfillmentPlanId },
) => {
  const { sellerLabelProviderId } = labelObject;
  const hasWalletProvider = Global.selectSellerLabelProviderIsWalleted(state, {
    sellerLabelProviderId,
  });
  trackLabelCreationComplete(
    fulfillmentPlanId,
    labelObject,
    doNotPrint,
    hasWalletProvider,
    getPlansWithDangerousGoods([fulfillmentPlanId], state),
  );

  createLabelSubject.next({
    fulfillmentPlanId,
    isCreatingLabel: false,
    errorMessages: [],
  });

  const modalId = Global.selectRateBrowsergetModalId(state);
  const fulfillmentPlanIdWithFallback = fulfillmentPlanId || Global.selectCreateLabelContentgetSelectedFulfillmentPlanId(state);
  ModalService.closeModalById(modalId);

  const launchShipmentDetails$ = of(ShipmentDetailsActions.launchShipmentDetail(fulfillmentPlanIdWithFallback));
  const closeCreateLabelModal$ =
    originatingFulfillmentPlanId === CUSTOMERS_CREATE_LABEL ? of(CustomersCreateLabelActions.closeCustomersCreateLabelModal()) : EMPTY;

  if (doNotPrint) {
    return concat(closeCreateLabelModal$, launchShipmentDetails$);
  }

  const printLabel$ = of(
    PrintActions.printLabelFromFulfillmentPlan({
      fulfillmentPlanId: fulfillmentPlanIdWithFallback,
    }),
  );

  return concat(closeCreateLabelModal$, launchShipmentDetails$, printLabel$);
};

export const createLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CREATE_LABEL_ACTION),
    throttleTime(1000),
    switchMap(({ fulfillmentPlanId, doNotPrint, returnLabelMetadata, labelMetadata }) => {
      const state = getState();

      const { insuranceProviderName } = labelMetadata;
      const selectedInsuranceOption =
        Object.values(InsuranceProviders).find((insuranceOption) => insuranceOption.name === insuranceProviderName) ||
        InsuranceProviders.None;
      const shouldShowShipsuranceTerms = !hasUserAcceptedInsuranceTerms(state, selectedInsuranceOption.insuranceProviderId);
      if (shouldShowShipsuranceTerms) {
        return launchShipsuranceTermsModalWaitToProceed(
          action$,
          Actions.createLabel({
            fulfillmentPlanId,
            doNotPrint,
            returnLabelMetadata,
            labelMetadata,
          }),
          selectedInsuranceOption.insuranceProviderId,
        );
      }

      const notificationId = Helpers.showCreatingLabelNotification({
        getState,
        serviceId: labelMetadata.serviceId,
      });
      createLabelSubject.next({ fulfillmentPlanId, isCreatingLabel: true });
      const { shippingConfiguration } = Global.selectCSWByFulfillmentPlanId(state, { fulfillmentPlanId });
      const metadata = {
        notificationId,
        fromSingleLabel: true,
        showPrintDialog: !doNotPrint,
        actionCreator: Actions.createLabelActionEnd,
        fulfillmentPlanId,
        isReturn: labelMetadata.isReturn,
      };
      const model = {
        shippingConfiguration: Builders.buildSaveShippingConfigurationAndRateRequest(state, {
          fulfillmentPlanId,
        }),
      };
      const sanitizedShippingConfiguration = {
        ...shippingConfiguration,
        ...ShipmentConfigurationService.mapModelToRequest(model),
      };

      if (labelMetadata.isReturn) {
        const createReturnLabelAction = () =>
          ReturnsActions.createReturnLabel({
            shippingConfiguration: sanitizedShippingConfiguration,
            emailTo: returnLabelMetadata ? returnLabelMetadata.emailAddresses : undefined,
            metadata,
          });

        return checkUpsTermsRequiredForReturn$({ state, fulfillmentPlanId }).pipe(
          switchMap(({ needsTerms }) => {
            if (needsTerms) {
              return showUpsTermsModalAndWait$(createReturnLabelAction);
            }
            return of(createReturnLabelAction());
          }),
        );
      }

      return Api.saveFulfillmentPlan(sanitizedShippingConfiguration).pipe(
        switchMap((newlyCreateFulfillmentPlanId) => {
          const setNewFulfillmentPlanId$ = of(Actions.setFulfillmentPlanId(newlyCreateFulfillmentPlanId));
          return concat(
            setNewFulfillmentPlanId$,
            merge(
              waitForEndOfCostReview(action$).pipe(
                takeUntil(action$.pipe(ofType(BulkPurchaseActions.BULK_PURCHASE_COST_REVIEW_CANCEL))),
                mergeMap(({ jobGuid }) => {
                  return concat(
                    of(
                      BulkActions.bulkSetActiveLabelBatchStatusStep({
                        step: LabelBatchStatusStep.Loading.ordinal,
                      }),
                    ),
                    of(BulkActions.clearCostReview(jobGuid)),
                  );
                }),
              ),
              runNowAndRetryAfterCostReview({
                action$,
              }).pipe(
                mergeMap(() => {
                  return makeBulkCreateLabelObservable({
                    fulfillmentPlanIds: [newlyCreateFulfillmentPlanId],
                    action$,
                    logContext: {
                      purchasePoint: 'rate-browser',
                      labelObject: sanitizedShippingConfiguration,
                    },
                    shouldThrowApiError: false,
                    getState,
                  }).pipe(
                    tap(() => NotificationService.removeNotification(notificationId)),
                    filter((next) => !next?.apiSuccess),
                    mergeMap(({ finalMessage, friendlyErrors, hasCostReviewError }) => {
                      if (finalMessage) {
                        return endProcessingSuccessForOutboundLabel(state, {
                          doNotPrint,
                          fulfillmentPlanId: newlyCreateFulfillmentPlanId,
                          originatingFulfillmentPlanId: fulfillmentPlanId,
                          labelObject: sanitizedShippingConfiguration,
                        });
                      }
                      if (hasCostReviewError) {
                        return merge(
                          startBulkPurchaseCostReview({
                            action$,
                            included: [newlyCreateFulfillmentPlanId],
                            excluded: undefined,
                            errored: undefined,
                            andPrint: true,
                            getState,
                            shouldShowLegacyCostReview: true,
                          }),
                          action$.pipe(
                            ofType(BulkPurchaseActions.BULK_PURCHASE_COST_REVIEW_CANCEL),
                            take(1),
                            takeUntil(waitForEndOfCostReview(action$)),
                            mergeMap(() => {
                              createLabelSubject.next({
                                fulfillmentPlanId,
                                isCreatingLabel: false,
                              });
                              return of(Actions.setCostReviewGuid(undefined));
                            }),
                          ),
                          action$.pipe(
                            ofType(PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_SUCCESS),
                            take(1),
                            takeUntil(
                              action$.pipe(
                                ofType(
                                  BulkPurchaseActions.BULK_PURCHASE_COST_REVIEW_CANCEL,
                                  PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_FAILURE,
                                ),
                                take(1),
                              ),
                            ),
                            mergeMap(({ jobGuid }) => {
                              const closeCreateLabelModal =
                                fulfillmentPlanId === CUSTOMERS_CREATE_LABEL
                                  ? of(CustomersCreateLabelActions.closeCustomersCreateLabelModal())
                                  : EMPTY;
                              const costReviewJobGuid = Global.selectCreateLabelContentCostReviewGuid(getState());
                              if (jobGuid === costReviewJobGuid) {
                                const modalId = Global.selectRateBrowsergetModalId(state);
                                ModalService.closeModalById(modalId);
                                return closeCreateLabelModal;
                              }
                              return EMPTY;
                            }),
                          ),
                        );
                      }
                      const shouldPrint = false; // was always hardcoded to false
                      return of(
                        // have to use the original fulfillmentPlanId for csw reducer
                        CSWActions.createLabelFailure(fulfillmentPlanId, friendlyErrors, shouldPrint, true),
                      );
                    }),
                    catchError(({ response }) => {
                      const { data = {} } = response;
                      const errorsForUser = getErrorMessagesFromSaveConfigAndRateResponse(data);
                      createLabelSubject.next({
                        fulfillmentPlanId,
                        isCreatingLabel: false,
                        errorMessages: errorsForUser,
                      });
                      trackLabelCreationError(fulfillmentPlanId, errorsForUser);
                      return of(BulkActions.bulkPurchaseLabelFailure([newlyCreateFulfillmentPlanId], errorsForUser, metadata));
                    }),
                  );
                }),
              ),
            ),
          );
        }),
        catchError((errorResponse) => {
          NotificationService.removeNotification(notificationId);
          const errors = get(errorResponse, 'response.data.Errors', emptyArray);
          return of(
            CSWActions.createLabelFailure(
              fulfillmentPlanId,
              errors.map((e) => e.Message),
              undefined,
              true,
            ),
          );
        }),
      );
    }),
  );
};

const endProcessingSuccessForInboundLabel = (state, fulfillmentPlanId, fulfillmentId, showPrintDialog) => {
  const modalId = Global.selectRateBrowsergetModalId(state);
  ModalService.closeModalById(modalId);

  if (showPrintDialog) {
    window.open(`/api/download/label/${fulfillmentId}`, '_blank');
  }
  const originatingFulfillmentPlanId = fulfillmentPlanId;
  const closeCreateLabelModal =
    originatingFulfillmentPlanId === CUSTOMERS_CREATE_LABEL ? of(CustomersCreateLabelActions.closeCustomersCreateLabelModal()) : EMPTY;

  return concat(
    of(Actions.setFulfillmentPlanId(undefined)),
    closeCreateLabelModal,
    of(ReturnsDetailsActions.goToReturnDetails(fulfillmentId)),
  );
};

export const endProcessingCreateLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CREATE_LABEL_ACTION_END),
    mergeMap(({ metadata }) => {
      // only returns from rate browser hit this epic now - 2/12/2021 tjc
      const state = getState();
      const { notificationId, fulfillmentId, fulfillmentPlanId, showPrintDialog } = metadata;

      NotificationService.removeNotification(notificationId);
      return endProcessingSuccessForInboundLabel(state, fulfillmentPlanId, fulfillmentId, showPrintDialog);
    }),
  );
};

export const createLabelFailure = (action$) => {
  return action$.pipe(
    ofType(BulkActions.BULK_PURCHASE_LABEL_FAILURE, ReturnsActions.RETURNS_CREATE_LABEL_FAILURE),
    mergeMap(({ errors, errorMessages, metadata }) => {
      const notificationId = get(metadata, 'notificationId');
      const isRateBrowserError = get(metadata, 'fulfillmentPlanId', '') === 'rate-browser';
      if (notificationId) {
        NotificationService.removeNotification(notificationId);
      }

      if (isRateBrowserError) {
        return of(CSWActions.createLabelFailure(metadata.fulfillmentPlanId, errors, true, true));
      }

      const NEED_EXCEPTION = 'NeedSubscriptionException';
      const ACTIVE_REQUIRED = 'ActiveSubscriptionRequired';

      const hasSubscriptionMessage = (errorArr) => {
        if (Array.isArray(errorArr)) {
          return errorArr.some((err) => {
            const message = err?.values?.originalMessage || err;
            if (typeof message?.includes === 'function') {
              return message.includes(NEED_EXCEPTION) || message.includes(ACTIVE_REQUIRED);
            }
            return false;
          });
        }
        return false;
      };

      const hasSubscriptionCodeOrMessage = (errorArr) => {
        if (Array.isArray(errorArr)) {
          return errorArr.some((err) => {
            return (err.Code && err.Code.includes(ACTIVE_REQUIRED)) || (err.Message && err.Message.includes(NEED_EXCEPTION));
          });
        }
        return false;
      };

      if (hasSubscriptionMessage(errors) || hasSubscriptionCodeOrMessage(errorMessages)) {
        ModalService.launchModal({
          component: SubscriptionRequiredModal,
        });
      }
      return EMPTY;
    }),
  );
};
